import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Column } from 'primereact/column';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import CommercialAgreementListHeader from './CommercialAgreementListHeader';
import { withGrowl } from '../../components/GrowlNotifications';
import { growlShape } from '../../components/prop-types';
import { toSortOrder, parse, sortUrl } from '../../utils/UrlUtil';
import Paginator from '../../components/Paginator';

const messages = defineMessages({
  promiseeName: { id: 'attrs.promisee-name', defaultMessage: 'Promisee Name' },
  documentContent: { id: 'attrs.document-content', defaultMessage: 'Document Content' },
  document: { id: 'attrs.document', defaultMessage: 'Document' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  action: { id: 'attrs.actions', defaultMessage: 'Actions' },
  success: { id: 'noti.success', defaultMessage: 'Success!' },
});

const nameTemplate = rowData => (
  <span>{rowData.promisee.displayName}</span>
);

const documentContentTemplate = rowData => (
  <FormattedMessage
    id="attrs.commercial-agreement.name"
    defaultMessage="{promiseeName} commercial agreement"
    values={{ promiseeName: rowData.promisee.displayName }}
  >
    {text => (
      <a href={rowData.uploadUrl} target="_blank" rel="noreferrer noopener">
        {text}
      </a>
    )}
  </FormattedMessage>
);

const createdTemplate = (rowData, { formatDate }) => {
  const created = formatDate(moment(rowData.createdOn), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return created;
};

class CommercialAgreementList extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteCommercialAgreement = this.handleDeleteCommercialAgreement.bind(this);
    this.actionsTemplate = this.actionsTemplate.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
  }

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  actionsTemplate(rowData, props) {
    return (
      <Button
        icon="pi pi-times"
        className="p-button-secondary"
        onClick={() => this.handleDeleteCommercialAgreement(rowData.id, props)}
      />
    );
  }

  async handleDeleteCommercialAgreement(id) {
    const {
      currentAccount,
      growl,
      intl: { formatMessage },
      deleteCommercialAgreement,
    } = this.props;

    try {
      await deleteCommercialAgreement({
        variables: {
          input: {
            accountId: currentAccount,
            commercialAgreementId: id,
          },
        },
        refetchQueries: ['listCommercialAgreements'],
      });
      growl.show({ severity: 'info', detail: formatMessage(messages.success) });
    } catch (err) {
      growl.show({ severity: 'error', sticky: true, detail: err.message });
    }
  }

  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      loading,
      growl,
      currentAccount,
      location,
    } = this.props;

    const connectionRows = edges.map(({ node }) => ({
      ...node,
      displayName: node.promisee ? node.promisee.displayName : '',
    }));

    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    const nameHeader = formatMessage(messages.promiseeName);
    const documentContentHeader = formatMessage(messages.documentContent);
    const createdHeader = formatMessage(messages.created);
    const actionHeader = formatMessage(messages.action);

    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel>
              <div className="p-grid">
                <div className="p-col-12">
                  <CommercialAgreementListHeader />
                </div>
                <div className="p-col-12 align-center">
                  <DataTable
                    value={connectionRows}
                    responsive
                    sortField={sortBy}
                    sortOrder={toSortOrder(sortDir)}
                    onSort={this.onSortChange}
                    lazy
                    loading={loading}
                    autoLayout
                  >
                    <Column
                      field="displayName"
                      header={nameHeader}
                      body={nameTemplate}
                      sortable
                    />
                    <Column
                      field="documentContent"
                      header={documentContentHeader}
                      body={documentContentTemplate}
                    />
                    <Column
                      field="createdOn"
                      header={createdHeader}
                      formatDate={formatDate}
                      body={createdTemplate}
                      sortable
                    />
                    <Column
                      header={actionHeader}
                      body={this.actionsTemplate}
                      currentAccount={currentAccount}
                      growl={growl}
                      formatMessage={formatMessage}
                      style={{ width: '3.5em' }}
                    />
                  </DataTable>
                </div>
                <div className="p-col-12 align-center">{footer}</div>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
}

CommercialAgreementList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  growl: growlShape.isRequired,
  currentAccount: PropTypes.string.isRequired,
  deleteCommercialAgreement: PropTypes.func.isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withGrowl(withRouter(injectIntl(CommercialAgreementList)));
